import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

export interface IVpfRequest {
  id: number;
  Action: string;
  VpfAmount: string;
  DeductionFromTheMonth: string;
  DeductionFromTheYear: string;
  VpfAcknowledge: string;
  is_draft: boolean;
  created_at?: string;
  modified_at?: string;
  [key: string]: any; // For additional fields from backend
}

interface IVpfState {
  data: IVpfRequest[];
  currentVpf: IVpfRequest | null;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  createLoading: boolean;
  total: number;
  page: number;
  limit: number;
  action: "list" | "create" | "update" | null;
}

const initialState: IVpfState = {
  data: [],
  currentVpf: null,
  loading: "idle",
  error: null,
  createLoading: false,
  total: 0,
  page: 1,
  limit: 10,
  action: null,
};

// Fetch all VPF requests
export const getAllVpfRequests = createAsyncThunk(
  "vpf/getAll",
  async (
    payload: {
      query?: string;
      page?: number;
      limit?: number;
      orderColumn?: string;
      orderBy?: string;
      status?: string;
      startDate?: string;
      endDate?: string;
    },
    thunkAPI
  ) => {
    try {
      const response = await Api.post(API_URLS.VPF.FIND_ALL, payload);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// Create or update VPF request
export const createOrUpdateVpfRequest = createAsyncThunk(
  "vpf/createOrUpdate",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(API_URLS.VPF.CREATE_OR_UPDATE, payload);
      return response.data;
    } catch (error: any) {
      console.log("Error in createOrUpdateVpfRequest:", error);
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const vpfSlice = createSlice({
  name: "vpf",
  initialState,
  reducers: {
    resetVpfState: (state) => {
      Object.assign(state, initialState);
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentVpf: (state) => {
      state.currentVpf = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(getAllVpfRequests.pending, (state) => {
        state.loading = "pending";
        state.error = null;
        state.action = "list";
      })
      .addCase(getAllVpfRequests.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        const responseData = action.payload.data || {};
        // The array is in responseData.data or responseData.vpf
        state.data = responseData.data || responseData.vpf || [];
        state.total = responseData.total || 0;
        state.page = responseData.page || 1;
        state.limit = responseData.limit || 10;
        state.action = "list";
      })
      .addCase(getAllVpfRequests.rejected, (state, action) => {
        state.loading = "rejected";
        state.error = action.payload as string;
        state.data = [];
        state.action = "list";
      })
      // Create or update
      .addCase(createOrUpdateVpfRequest.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createOrUpdateVpfRequest.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(createOrUpdateVpfRequest.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetVpfState, clearError, clearCurrentVpf } = vpfSlice.actions;
export default vpfSlice.reducer;
